import { MutableRefObject, useEffect, useRef } from 'react';

import { GridApi, GridRowId, GridValidRowModel } from '@mui/x-data-grid-premium';

export interface UseScrollToRowParams<R extends GridValidRowModel> {
  scrollToRow?: { rowId: GridRowId; colId?: string; token: number } | null;
  scrollToRowId?: GridRowId;
  rows: R[];
  apiRef: MutableRefObject<GridApi>;
  isHeadersLoading: boolean;
}

const useScrollToRow = <R extends GridValidRowModel>({
  scrollToRow,
  scrollToRowId,
  rows,
  apiRef,
  isHeadersLoading,
}: UseScrollToRowParams<R>): void => {
  useEffect(() => {
    if (!scrollToRowId) return;
    const api = apiRef.current;
    if (!api || typeof api.getAllRowIds !== 'function') return;
    const timeoutId = setTimeout(() => {
      const allIds = api.getAllRowIds();
      const rowIndex = allIds.findIndex((id) => String(id) === String(scrollToRowId));
      if (rowIndex >= 0) {
        api.scrollToIndexes({ rowIndex });
      }
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [scrollToRowId, rows, apiRef]);

  const lastScrolledToken = useRef<number | null>(null);

  useEffect(() => {
    if (!scrollToRow || rows.length === 0) return;
    if (lastScrolledToken.current === scrollToRow.token) return;
    if (isHeadersLoading) return;
    const api = apiRef.current;
    if (!api?.getRowIndexRelativeToVisibleRows || !api?.scrollToIndexes) return;

    let frameId = 0;
    let attempts = 0;
    const rowSelector = `.MuiDataGrid-row[data-id="${CSS.escape(String(scrollToRow.rowId))}"]`;

    const tick = () => {
      if (++attempts > 90) return;
      const rowIndex = api.getRowIndexRelativeToVisibleRows(scrollToRow.rowId);
      if (rowIndex == null || rowIndex < 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const colIndex = scrollToRow.colId
        ? api.getVisibleColumns().findIndex((col) => col.field === scrollToRow.colId)
        : -1;
      if (scrollToRow.colId && colIndex < 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      api.scrollToIndexes(colIndex >= 0 ? { rowIndex, colIndex } : { rowIndex });

      const root: ParentNode = api.rootElementRef?.current ?? document;
      const rowEl = root.querySelector<HTMLElement>(rowSelector);
      const scroller = rowEl?.closest<HTMLElement>('.MuiDataGrid-virtualScroller');
      if (!rowEl || !scroller) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        const settledRow = root.querySelector<HTMLElement>(rowSelector);
        const settledScroller = settledRow?.closest<HTMLElement>('.MuiDataGrid-virtualScroller');
        if (settledRow && settledScroller) {
          const scrollerRect = settledScroller.getBoundingClientRect();
          const rowRect = settledRow.getBoundingClientRect();
          const rowTopWithin = rowRect.top - scrollerRect.top + settledScroller.scrollTop;
          const desired = Math.max(
            0,
            rowTopWithin - (settledScroller.clientHeight - rowRect.height) / 2,
          );
          settledScroller.scrollTo({ top: desired, left: settledScroller.scrollLeft });
        }
        lastScrolledToken.current = scrollToRow.token;
      });
    };
    frameId = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frameId);
  }, [scrollToRow, rows, apiRef, isHeadersLoading]);
};

export default useScrollToRow;
